import React, { useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    parseFooterContentInBrowser,
    type ParsedFooterFAQContent,
} from '~/modules/professionals-category/util/parse-footer-content-in-browser';
import { replaceEmDashes } from '~/modules/professionals-category/util/replace-em-dashes';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';

/**
 * Interface representing a single FAQ element with title, question and answer
 * @interface FAQElement
 * @property {string} content - The full content of the FAQ
 * @property {string} title - The title/heading of the FAQ
 * @property {string} question - The FAQ question text
 * @property {string} answer - The FAQ answer text
 */
interface FAQElement {
    content: string;
    title: string;
    question?: string;
    answer?: string;
}

/**
 * Props interface for the CategoryPageFAQ component
 * @interface CategoryPageFAQProps
 * @property {FAQElement[]} faqSchemas - Array of FAQ items to display
 * @property {string} footerContent - HTML content to display in the header/footer section
 * @property {'accordion' | 'grid'} [variant='accordion'] - Display variant
 * @property {string} [categoryLabel] - Category label for grid variant header
 * @property {string} [seoHeading] - Visible h2 for the category SEO copy block
 * @property {ParsedFooterFAQContent} [parsedFooterContent] - Server-parsed footer for grid SSR
 */
interface CategoryPageFAQProps {
    faqSchemas: FAQElement[];
    footerContent: string;
    variant?: 'accordion' | 'grid';
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
}

/**
 * Grid variant component for static FAQ display (used on products pages)
 * Parses footerContent HTML to extract intro text and FAQ items
 * Displays FAQs in a newspaper-style 3-column layout
 */
const FAQGridVariant = ({
    footerContent,
    categoryLabel,
    seoHeading,
    parsedFooterContent,
}: {
    footerContent: string;
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
}) => {
    // Flip to light text on the dark projects browse theme.
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const textClass = isDark ? 'text-white' : 'text-black';
    const borderClass = isDark ? 'border-white/15' : 'border-gray-200';

    const clientParsedContent = useMemo(
        () => parseFooterContentInBrowser(footerContent),
        [footerContent],
    );
    const parsedContent = parsedFooterContent ?? clientParsedContent;

    const introHtml = replaceEmDashes(parsedContent.introHtml);
    const faqItems = parsedContent.faqItems.map((faq) => ({
        title: replaceEmDashes(faq.title),
        content: replaceEmDashes(faq.content),
    }));
    const cleanedFooterContent = replaceEmDashes(footerContent);
    const showRawFallback =
        !introHtml && faqItems.length === 0 && Boolean(footerContent);

    if (
        !introHtml &&
        faqItems.length === 0 &&
        !categoryLabel &&
        !showRawFallback
    ) {
        return null;
    }

    if (showRawFallback) {
        return (
            <div
                className={cn(
                    'flex flex-col gap-14.5 px-6 md:px-12',
                    textClass,
                )}
            >
                {seoHeading && (
                    <h2
                        className={cn(
                            'text-6 font-medium tracking-[0.025rem]',
                            textClass,
                        )}
                    >
                        {seoHeading}
                    </h2>
                )}
                {categoryLabel && (
                    <div className={cn('border-t pt-4', borderClass)}>
                        <span
                            className={cn(
                                'text-4 font-normal tracking-[-0.03em]',
                                textClass,
                            )}
                        >
                            {categoryLabel}
                        </span>
                    </div>
                )}
                <div
                    className="faq-footer"
                    dangerouslySetInnerHTML={{ __html: cleanedFooterContent }}
                />
                <div className={cn('border-t pt-4', borderClass)} />
            </div>
        );
    }

    return (
        <div className={cn('flex flex-col gap-14.5 px-6 md:px-12', textClass)}>
            {seoHeading && (
                <h2
                    className={cn(
                        'text-6 font-medium tracking-[0.025rem]',
                        textClass,
                    )}
                >
                    {seoHeading}
                </h2>
            )}
            {/* Header Section - category label and hero intro */}
            {(categoryLabel || introHtml) && (
                <div className="flex flex-col gap-11">
                    {categoryLabel && (
                        <div className={cn('border-t pt-4', borderClass)}>
                            <span
                                className={cn(
                                    'text-4 font-normal tracking-[-0.03em]',
                                    textClass,
                                )}
                            >
                                {categoryLabel}
                            </span>
                        </div>
                    )}
                    {introHtml && (
                        <div
                            className={cn(
                                'cms-inline-html faq-grid-intro font-twk-ghost text-[2.15rem] leading-[1.4] font-normal tracking-tight md:max-w-370',
                                textClass,
                            )}
                            dangerouslySetInnerHTML={{ __html: introHtml }}
                        />
                    )}
                </div>
            )}

            {/* FAQ Columns - newspaper-style layout flowing down then across */}
            {faqItems.length > 0 && (
                <div className="columns-1 gap-21.5 md:max-w-370 md:columns-3">
                    {faqItems.map((faq, index) => (
                        <div
                            key={`faq-grid-${index}`}
                            className="mb-10 flex break-inside-avoid flex-col gap-1.5 md:max-w-109"
                        >
                            <h3
                                className={cn(
                                    'text-3.5 leading-normal font-semibold tracking-[0.02em]',
                                    textClass,
                                )}
                            >
                                {faq.title}
                            </h3>
                            {faq.content && (
                                <div
                                    className={cn(
                                        'cms-inline-html faq-grid-answer text-3.5 leading-[1.4] font-normal tracking-[0.02em]',
                                        textClass,
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: faq.content,
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className={cn('border-t pt-4', borderClass)} />
        </div>
    );
};

/**
 * Component that renders an FAQ section with expandable accordion items
 * or as a static grid layout depending on the variant prop
 * @param {CategoryPageFAQProps} props - Component props
 * @returns {React.JSX.Element | null} The rendered FAQ section or null if no content
 */
export const CategoryPageFAQ = (props: CategoryPageFAQProps) => {
    const {
        faqSchemas,
        footerContent,
        variant = 'accordion',
        categoryLabel,
        seoHeading,
        parsedFooterContent,
    } = props;
    // Flip to light text on the dark projects browse theme.
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const textClass = isDark ? 'text-white' : 'text-black';
    const borderClass = isDark ? 'border-white/15' : 'border-gray-200';

    // For accordion variant, filter faqSchemas
    const filteredFAQSchemas = React.useMemo(
        () => faqSchemas.filter((faq) => faq.question && faq.answer),
        [faqSchemas],
    );

    // Which questions are open. Tracked in state (with onToggle below) rather
    // than passing a fixed `open` per row: a bare `open` prop makes native
    // `details` controlled, so without a handler React re-asserts it on the next
    // render and reverts the user's expand/collapse. Seeded open on the first
    // question only when there's no footer prose acting as the section's opening
    // content — with prose present every question starts collapsed. (An older
    // default referenced a 'footerContent' accordion item long since extracted,
    // which meant "all closed" whenever footer prose existed; this keeps that
    // shipped behaviour, now deliberately.)
    const [openFAQs, setOpenFAQs] = React.useState<Set<number>>(() =>
        footerContent ? new Set() : new Set([0]),
    );
    const handleFAQToggle =
        (index: number) =>
        (event: React.SyntheticEvent<HTMLDetailsElement>) => {
            const isOpen = event.currentTarget.open;
            setOpenFAQs((prev) => {
                const next = new Set(prev);
                if (isOpen) next.add(index);
                else next.delete(index);
                return next;
            });
        };

    // Grid variant - parses footerContent to extract FAQ items
    if (variant === 'grid') {
        if (!footerContent) {
            return null;
        }
        return (
            <FAQGridVariant
                footerContent={footerContent}
                categoryLabel={categoryLabel}
                seoHeading={seoHeading}
                parsedFooterContent={parsedFooterContent}
            />
        );
    }

    // Early return if no content for accordion variant
    if (filteredFAQSchemas.length === 0 && !footerContent) {
        return null;
    }

    return (
        <div className={cn('px-6 md:mb-16 md:px-12', textClass)}>
            <div className="flex flex-col pb-14.5 md:gap-12 md:pb-0">
                {seoHeading && footerContent && (
                    <h2
                        className={cn(
                            'pt-6 text-6 font-medium tracking-[0.025rem] md:pt-0',
                            textClass,
                        )}
                    >
                        {seoHeading}
                    </h2>
                )}
                {/* Footer content renders in full — it is the SEO prose, and
                    mobile-first indexing is where it earns its keep. No clamp,
                    no disclosure. */}
                {footerContent && (
                    <div
                        className="cms-inline-html faq-footer"
                        dangerouslySetInnerHTML={{
                            __html: replaceEmDashes(footerContent),
                        }}
                    />
                )}

                {/* Render FAQs only if present. Native details/summary rather
                    than the Rocco Accordion: Radix unmounts closed item
                    content, which would strip the answers (and any links in
                    them) from the served DOM. Here each answer stays mounted in
                    both states — only display is toggled — matching the
                    footer-prose disclosure above. Styling mirrors the previous
                    accordion rows: bottom-bordered items, py-3 trigger rows and
                    a plus/minus indicator. */}
                {filteredFAQSchemas.length > 0 && (
                    <div>
                        <h4 className="faq-footer-title">FAQs</h4>
                        {filteredFAQSchemas.map((faq, index) => (
                            <details
                                key={faq.title}
                                open={openFAQs.has(index)}
                                onToggle={handleFAQToggle(index)}
                                className="group/faq border-b border-b-gray-900/30"
                            >
                                {/* flex! beats Aria's un-layered summary{display:list-item} normalize */}
                                <summary className="flex! min-h-11 cursor-pointer list-none items-center justify-between gap-3 py-3 font-medium marker:hidden [&::-webkit-details-marker]:hidden">
                                    <span
                                        className="text-left text-xl font-medium"
                                        dangerouslySetInnerHTML={{
                                            __html: faq.title,
                                        }}
                                    />
                                    <Plus
                                        aria-hidden="true"
                                        className="h-4 w-4 shrink-0 group-open/faq:hidden"
                                    />
                                    <Minus
                                        aria-hidden="true"
                                        className="hidden h-4 w-4 shrink-0 group-open/faq:block"
                                    />
                                </summary>
                                <div className="pb-4">
                                    <div
                                        className="cms-inline-html grid py-2"
                                        dangerouslySetInnerHTML={{
                                            __html: faq.answer || '',
                                        }}
                                    />
                                </div>
                            </details>
                        ))}
                    </div>
                )}
            </div>
            <div className={cn('border-t pt-4', borderClass)} />
        </div>
    );
};
